import {
  OnboardingDialogOption,
  openBankAccountDialog,
  openLegalNameDialog,
} from "@components/utils/OnboardingDialog/utils";
import { Paths } from "@constants/paths";
import { PostLoginInfoType } from "@types";
import { MemberSettingFundingMode } from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthSession from "./useAuthSession";
import { useMemberSettings } from "./useMemberSettings";
/**
 * After login/register, user need to provide required info
 * There are 2 types of required info based on member settings fundingMode:
 * 1. Bank account
 * 2. Legal name
 *
 * return {
 *  type: PostLoginInfoType | null,
 *  isFulfilled: boolean,
 *  triggerForm: trigger the form to update the info
 * }
 */
const usePostLoginInfo = () => {
  const { fundingMode } = useMemberSettings();
  const { bankAccount, legalName } = useAuthSession();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;

  const requiredInfo = useMemo(() => {
    let type: PostLoginInfoType | null = null;
    let isFulfilled = false;

    switch (fundingMode) {
      case MemberSettingFundingMode.BankOnly:
        type = PostLoginInfoType.BANK_ACCOUNT;
        isFulfilled = !!bankAccount;
        break;
      case MemberSettingFundingMode.EWalletOnly:
        type = PostLoginInfoType.LEGAL_NAME;
        isFulfilled = !!legalName;
        break;
      default:
        type = PostLoginInfoType.BANK_ACCOUNT;
        isFulfilled = !!bankAccount;
        break;
    }

    return {
      type,
      isFulfilled,
    };
  }, [fundingMode, bankAccount, legalName]);

  const triggerForm = useCallback(
    (formType: "dialog" | "full-page", onUpdateSuccess?: () => void, options?: OnboardingDialogOption) => {
      if (formType === "dialog") {
        switch (requiredInfo.type) {
          case PostLoginInfoType.BANK_ACCOUNT:
            // ignore drawer if already in page mode
            if (pathname === Paths.PRIVATE.AUTH_BANK_ACCOUNT_PAGE) return;
            openBankAccountDialog(onUpdateSuccess, options);
            break;
          case PostLoginInfoType.LEGAL_NAME:
            // ignore drawer if already in page mode
            if (pathname === Paths.PRIVATE.AUTH_LEGAL_NAME_PAGE) return;
            openLegalNameDialog(onUpdateSuccess, options);
            break;
        }
      }
      if (formType === "full-page") {
        switch (requiredInfo.type) {
          case PostLoginInfoType.BANK_ACCOUNT:
            navigate(Paths.PRIVATE.AUTH_BANK_ACCOUNT_PAGE, {
              replace: true,
            });
            break;
          case PostLoginInfoType.LEGAL_NAME:
            navigate(Paths.PRIVATE.AUTH_LEGAL_NAME_PAGE, {
              replace: true,
            });
            break;
        }
      }
    },
    [requiredInfo.type, navigate, pathname],
  );

  return {
    ...requiredInfo,
    triggerForm,
  };
};

export default usePostLoginInfo;
